import {
  getLeoUuid,
  LeoUUID,
  getString,
  getObject,
  getOptionalString,
  getOptionalObject,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { SurveyLocalizedText, SurveyLocalizedTextDTO } from "../assets/surveyLocalizedText";
import { SurveySettingDialogProperties, SurveySettingDialogPropertiesDTO } from "../types/surveySettingDialogProperties";
import { RespondentQuestionDetails, RespondentQuestionDetailsDTO } from "./respondentQuestionDetails";
import { ProgressPercentage, ProgressPercentageDTO } from "./progressPercentage";
/* eslint-disable import/export */
export abstract class SaveAndGetNextQuestionRPC implements
  LeoRPC<SaveAndGetNextQuestionRPC.Request, SaveAndGetNextQuestionRPC.Response, SaveAndGetNextQuestionRPC.Errors.Errors>
{
  abstract execute(
    request: SaveAndGetNextQuestionRPC.Request
  ): Promise<LeoRPCResult<SaveAndGetNextQuestionRPC.Response, SaveAndGetNextQuestionRPC.Errors.Errors>>;
}

export namespace SaveAndGetNextQuestionRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    currentQuestionId: string;
    surveyLinkId: string;
    answerJson: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isSaveAndGetNextQuestionRPCRequest: boolean = true;
  
    readonly currentQuestionId: LeoUUID;
    readonly surveyLinkId: LeoUUID;
    readonly answerJson: string;
  
  
    constructor(
    currentQuestionId: LeoUUID, 
    surveyLinkId: LeoUUID, 
    answerJson: string
    ) {
      this.currentQuestionId = currentQuestionId;
      this.surveyLinkId = surveyLinkId;
      Request.validateAnswerJson(answerJson);
      this.answerJson = answerJson;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const currentQuestionId = getLeoUuid(dto, "currentQuestionId", InvalidRequestCurrentQuestionIdError);
      const surveyLinkId = getLeoUuid(dto, "surveyLinkId", InvalidRequestSurveyLinkIdError);
      const answerJson = getString(dto, "answerJson", InvalidRequestAnswerJsonError);
      return new Request(
        currentQuestionId,
        surveyLinkId,
        answerJson
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          currentQuestionId: this.currentQuestionId.toDTO(),
          surveyLinkId: this.surveyLinkId.toDTO(),
          answerJson: this.answerJson
      };
      return dto;
    }
    private static validateAnswerJson = (answerJson: string): void => {
      if(answerJson.trim().length === 0) {
        throw new InvalidRequestAnswerJsonNonEmptyError(
          `Attribute answerJson cannot be an empty string.`
        );
      }
    }
    
  }
  
  Request.prototype.toString = function toString(): string {
    return `SaveAndGetNextQuestionRPC(currentQuestionId='${this.currentQuestionId}',surveyLinkId='${this.surveyLinkId}',answerJson='${this.answerJson}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  
  export namespace ResponseEnums {
    export namespace NextQuestionStatus {
      
      export namespace AllQuestionsAttemptedEnums {
        export namespace NextAction {
          export interface ThankYouPageDTO {
            case: string;
            thankYouPageText: SurveyLocalizedTextDTO;
          }
          
          export class ThankYouPage {
            readonly isNextActionThankYouPage: boolean = true;
          
            readonly thankYouPageText: SurveyLocalizedText;
          
          
            constructor(
            thankYouPageText: SurveyLocalizedText
            ) {
              this.thankYouPageText = thankYouPageText;
            }
          
            static fromDTO(dto: ThankYouPageDTO): ThankYouPage {
              const thankYouPageText = SurveyLocalizedText.fromDTO(getObject(dto, "thankYouPageText", InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionThankYouPageThankYouPageTextError));
              return new ThankYouPage(
                thankYouPageText
              );
            }
          
            toDTO(): ThankYouPageDTO {
              const dto: ThankYouPageDTO = {
                case: "THANK_YOU_PAGE",
                  thankYouPageText: this.thankYouPageText.toDTO()
              };
              return dto;
            }
            }
          
          ThankYouPage.prototype.toString = function toString(): string {
            return `NextAction.THANK_YOU_PAGE(thankYouPageText='${this.thankYouPageText}')`;
          };
          
          export interface SubmitConfirmationDTO {
            case: string;
            submitConfirmationDialogProperties: SurveySettingDialogPropertiesDTO;
          }
          
          export class SubmitConfirmation {
            readonly isNextActionSubmitConfirmation: boolean = true;
          
            readonly submitConfirmationDialogProperties: SurveySettingDialogProperties;
          
          
            constructor(
            submitConfirmationDialogProperties: SurveySettingDialogProperties
            ) {
              this.submitConfirmationDialogProperties = submitConfirmationDialogProperties;
            }
          
            static fromDTO(dto: SubmitConfirmationDTO): SubmitConfirmation {
              const submitConfirmationDialogProperties = SurveySettingDialogProperties.fromDTO(getObject(dto, "submitConfirmationDialogProperties", InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionSubmitConfirmationSubmitConfirmationDialogPropertiesError));
              return new SubmitConfirmation(
                submitConfirmationDialogProperties
              );
            }
          
            toDTO(): SubmitConfirmationDTO {
              const dto: SubmitConfirmationDTO = {
                case: "SUBMIT_CONFIRMATION",
                  submitConfirmationDialogProperties: this.submitConfirmationDialogProperties.toDTO()
              };
              return dto;
            }
            }
          
          SubmitConfirmation.prototype.toString = function toString(): string {
            return `NextAction.SUBMIT_CONFIRMATION(submitConfirmationDialogProperties='${this.submitConfirmationDialogProperties}')`;
          };
          
          
          export namespace PromptUnansweredQuestionEnums {
            export namespace ProceedAction {
              export interface SubmitConfirmationDTO {
                case: string;
                submitConfirmationDialogProperties: SurveySettingDialogPropertiesDTO;
              }
              
              export class SubmitConfirmation {
                readonly isProceedActionSubmitConfirmation: boolean = true;
              
                readonly submitConfirmationDialogProperties: SurveySettingDialogProperties;
              
              
                constructor(
                submitConfirmationDialogProperties: SurveySettingDialogProperties
                ) {
                  this.submitConfirmationDialogProperties = submitConfirmationDialogProperties;
                }
              
                static fromDTO(dto: SubmitConfirmationDTO): SubmitConfirmation {
                  const submitConfirmationDialogProperties = SurveySettingDialogProperties.fromDTO(getObject(dto, "submitConfirmationDialogProperties", InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionSubmitConfirmationSubmitConfirmationDialogPropertiesError));
                  return new SubmitConfirmation(
                    submitConfirmationDialogProperties
                  );
                }
              
                toDTO(): SubmitConfirmationDTO {
                  const dto: SubmitConfirmationDTO = {
                    case: "SUBMIT_CONFIRMATION",
                      submitConfirmationDialogProperties: this.submitConfirmationDialogProperties.toDTO()
                  };
                  return dto;
                }
                }
              
              SubmitConfirmation.prototype.toString = function toString(): string {
                return `ProceedAction.SUBMIT_CONFIRMATION(submitConfirmationDialogProperties='${this.submitConfirmationDialogProperties}')`;
              };
              
              export interface SubmitSurveyDTO {
                case: string;
              }
              
              export class SubmitSurvey {
                readonly isProceedActionSubmitSurvey: boolean = true;
              
              
              
              
              
                toDTO(): SubmitSurveyDTO {
                  const dto: SubmitSurveyDTO = {
                    case: "SUBMIT_SURVEY",
                  };
                  return dto;
                }
                }
              
              SubmitSurvey.prototype.toString = function toString(): string {
                return `ProceedAction.SUBMIT_SURVEY()`;
              };
              
            
              export type ProceedAction = SubmitConfirmation | SubmitSurvey;
            
              export type ProceedActionDTO = SubmitConfirmationDTO | SubmitSurveyDTO;
            
              export const fromDTO = (dto: ProceedActionDTO): ProceedAction => {
                switch (dto.case) {
                  case "SUBMIT_CONFIRMATION":
                      return SubmitConfirmation.fromDTO(dto as SubmitConfirmationDTO);
                  case "SUBMIT_SURVEY":
                      return new SubmitSurvey();
                  default:
                    throw new InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionProceedActionError(
                        `Case ${dto.case} is not valid case of ProceedAction.`
                    );
                }
              };
            }
            
          }
          export interface PromptUnansweredQuestionDTO {
            case: string;
            promptUnansweredQuestionsDialogProperties: SurveySettingDialogPropertiesDTO;
            proceedAction: PromptUnansweredQuestionEnums.ProceedAction.ProceedActionDTO;
          }
          
          export class PromptUnansweredQuestion {
            readonly isNextActionPromptUnansweredQuestion: boolean = true;
          
            readonly promptUnansweredQuestionsDialogProperties: SurveySettingDialogProperties;
            readonly proceedAction: PromptUnansweredQuestionEnums.ProceedAction.ProceedAction;
          
          
            constructor(
            promptUnansweredQuestionsDialogProperties: SurveySettingDialogProperties, 
            proceedAction: PromptUnansweredQuestionEnums.ProceedAction.ProceedAction
            ) {
              this.promptUnansweredQuestionsDialogProperties = promptUnansweredQuestionsDialogProperties;
              this.proceedAction = proceedAction;
            }
          
            static fromDTO(dto: PromptUnansweredQuestionDTO): PromptUnansweredQuestion {
              const promptUnansweredQuestionsDialogProperties = SurveySettingDialogProperties.fromDTO(getObject(dto, "promptUnansweredQuestionsDialogProperties", InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionPromptUnansweredQuestionsDialogPropertiesError));
              const proceedAction = PromptUnansweredQuestionEnums.ProceedAction.fromDTO(getObject(dto, "proceedAction", InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionProceedActionError));
              return new PromptUnansweredQuestion(
                promptUnansweredQuestionsDialogProperties,
                proceedAction
              );
            }
          
            toDTO(): PromptUnansweredQuestionDTO {
              const dto: PromptUnansweredQuestionDTO = {
                case: "PROMPT_UNANSWERED_QUESTION",
                  promptUnansweredQuestionsDialogProperties: this.promptUnansweredQuestionsDialogProperties.toDTO(),
                  proceedAction: this.proceedAction.toDTO()
              };
              return dto;
            }
            }
          
          PromptUnansweredQuestion.prototype.toString = function toString(): string {
            return `NextAction.PROMPT_UNANSWERED_QUESTION(promptUnansweredQuestionsDialogProperties='${this.promptUnansweredQuestionsDialogProperties}',proceedAction='${this.proceedAction}')`;
          };
          
        
          export type NextAction = ThankYouPage | SubmitConfirmation | PromptUnansweredQuestion;
        
          export type NextActionDTO = ThankYouPageDTO | SubmitConfirmationDTO | PromptUnansweredQuestionDTO;
        
          export const fromDTO = (dto: NextActionDTO): NextAction => {
            switch (dto.case) {
              case "THANK_YOU_PAGE":
                  return ThankYouPage.fromDTO(dto as ThankYouPageDTO);
              case "SUBMIT_CONFIRMATION":
                  return SubmitConfirmation.fromDTO(dto as SubmitConfirmationDTO);
              case "PROMPT_UNANSWERED_QUESTION":
                  return PromptUnansweredQuestion.fromDTO(dto as PromptUnansweredQuestionDTO);
              default:
                throw new InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionError(
                    `Case ${dto.case} is not valid case of NextAction.`
                );
            }
          };
        }
        
      }
      export interface AllQuestionsAttemptedDTO {
        case: string;
        nextAction: AllQuestionsAttemptedEnums.NextAction.NextActionDTO;
      }
      
      export class AllQuestionsAttempted {
        readonly isNextQuestionStatusAllQuestionsAttempted: boolean = true;
      
        readonly nextAction: AllQuestionsAttemptedEnums.NextAction.NextAction;
      
      
        constructor(
        nextAction: AllQuestionsAttemptedEnums.NextAction.NextAction
        ) {
          this.nextAction = nextAction;
        }
      
        static fromDTO(dto: AllQuestionsAttemptedDTO): AllQuestionsAttempted {
          const nextAction = AllQuestionsAttemptedEnums.NextAction.fromDTO(getObject(dto, "nextAction", InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionError));
          return new AllQuestionsAttempted(
            nextAction
          );
        }
      
        toDTO(): AllQuestionsAttemptedDTO {
          const dto: AllQuestionsAttemptedDTO = {
            case: "ALL_QUESTIONS_ATTEMPTED",
              nextAction: this.nextAction.toDTO()
          };
          return dto;
        }
        }
      
      AllQuestionsAttempted.prototype.toString = function toString(): string {
        return `NextQuestionStatus.ALL_QUESTIONS_ATTEMPTED(nextAction='${this.nextAction}')`;
      };
      
      export interface NextQuestionDTO {
        case: string;
        respondentQuestionDetails: RespondentQuestionDetailsDTO;
        backtrackedAnswerJson?: string | null;
        submitConfirmationButtonText?: SurveyLocalizedTextDTO | null;
      }
      
      export class NextQuestion {
        readonly isNextQuestionStatusNextQuestion: boolean = true;
      
        readonly respondentQuestionDetails: RespondentQuestionDetails;
        readonly backtrackedAnswerJson: string | null;
        readonly submitConfirmationButtonText: SurveyLocalizedText | null;
      
      
        constructor(
        respondentQuestionDetails: RespondentQuestionDetails, 
        backtrackedAnswerJson: string | null = null, 
        submitConfirmationButtonText: SurveyLocalizedText | null = null
        ) {
          this.respondentQuestionDetails = respondentQuestionDetails;
          if ( backtrackedAnswerJson !== undefined && backtrackedAnswerJson !== null) {
            NextQuestion.validateBacktrackedAnswerJson(backtrackedAnswerJson);
          }
          this.backtrackedAnswerJson = backtrackedAnswerJson;
          this.submitConfirmationButtonText = submitConfirmationButtonText;
        }
      
        static fromDTO(dto: NextQuestionDTO): NextQuestion {
          const respondentQuestionDetails = RespondentQuestionDetails.fromDTO(getObject(dto, "respondentQuestionDetails", InvalidResponseNextQuestionStatusNextQuestionRespondentQuestionDetailsError));
          const backtrackedAnswerJson = getOptionalString(dto, "backtrackedAnswerJson", InvalidResponseNextQuestionStatusNextQuestionBacktrackedAnswerJsonError);
          let submitConfirmationButtonText = getOptionalObject(dto, "submitConfirmationButtonText");
          if(submitConfirmationButtonText !== undefined && submitConfirmationButtonText !== null) {
            submitConfirmationButtonText = SurveyLocalizedText.fromDTO(getObject(dto, "submitConfirmationButtonText", InvalidResponseNextQuestionStatusNextQuestionSubmitConfirmationButtonTextError));
          }
          return new NextQuestion(
            respondentQuestionDetails,
            backtrackedAnswerJson,
            submitConfirmationButtonText
          );
        }
      
        toDTO(): NextQuestionDTO {
          const dto: NextQuestionDTO = {
            case: "NEXT_QUESTION",
              respondentQuestionDetails: this.respondentQuestionDetails.toDTO(),
          };
          if (this.backtrackedAnswerJson !== undefined && this.backtrackedAnswerJson !== null){
            dto.backtrackedAnswerJson = this.backtrackedAnswerJson;
          }
          if (this.submitConfirmationButtonText !== undefined && this.submitConfirmationButtonText !== null){
            dto.submitConfirmationButtonText = this.submitConfirmationButtonText.toDTO();
          }
          return dto;
        }
        private static validateBacktrackedAnswerJson = (backtrackedAnswerJson: string): void => {
          if(backtrackedAnswerJson.trim().length === 0) {
            throw new InvalidResponseNextQuestionStatusNextQuestionBacktrackedAnswerJsonNonEmptyError(
              `Attribute backtrackedAnswerJson cannot be an empty string.`
            );
          }
        }
        
      }
      
      NextQuestion.prototype.toString = function toString(): string {
        return `NextQuestionStatus.NEXT_QUESTION(respondentQuestionDetails='${this.respondentQuestionDetails}',backtrackedAnswerJson='${this.backtrackedAnswerJson}',submitConfirmationButtonText='${this.submitConfirmationButtonText}')`;
      };
      
    
      export type NextQuestionStatus = AllQuestionsAttempted | NextQuestion;
    
      export type NextQuestionStatusDTO = AllQuestionsAttemptedDTO | NextQuestionDTO;
    
      export const fromDTO = (dto: NextQuestionStatusDTO): NextQuestionStatus => {
        switch (dto.case) {
          case "ALL_QUESTIONS_ATTEMPTED":
              return AllQuestionsAttempted.fromDTO(dto as AllQuestionsAttemptedDTO);
          case "NEXT_QUESTION":
              return NextQuestion.fromDTO(dto as NextQuestionDTO);
          default:
            throw new InvalidResponseNextQuestionStatusError(
                `Case ${dto.case} is not valid case of NextQuestionStatus.`
            );
        }
      };
    }
    
  }
  export interface ResponseDTO {
    nextQuestionStatus: ResponseEnums.NextQuestionStatus.NextQuestionStatusDTO;
    progressPercentage: ProgressPercentageDTO;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isSaveAndGetNextQuestionResponse: boolean = true;
  
    readonly nextQuestionStatus: ResponseEnums.NextQuestionStatus.NextQuestionStatus;
    readonly progressPercentage: ProgressPercentage;
  
  
    constructor(
    nextQuestionStatus: ResponseEnums.NextQuestionStatus.NextQuestionStatus, 
    progressPercentage: ProgressPercentage
    ) {
      this.nextQuestionStatus = nextQuestionStatus;
      this.progressPercentage = progressPercentage;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const nextQuestionStatus = ResponseEnums.NextQuestionStatus.fromDTO(getObject(dto, "nextQuestionStatus", InvalidResponseNextQuestionStatusError));
      const progressPercentage = ProgressPercentage.fromDTO(getObject(dto, "progressPercentage", InvalidResponseProgressPercentageError));
      return new Response(
        nextQuestionStatus,
        progressPercentage
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          nextQuestionStatus: this.nextQuestionStatus.toDTO(),
          progressPercentage: this.progressPercentage.toDTO()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `SaveAndGetNextQuestion(nextQuestionStatus='${this.nextQuestionStatus}',progressPercentage='${this.progressPercentage}')`;
  };
  

  export enum RPCError {
    InvalidSurveyLinkId = "INVALID_SURVEY_LINK_ID",
    InvalidQuestionId = "INVALID_QUESTION_ID",
    SurveyIsClosed = "SURVEY_IS_CLOSED",
    InvalidAnswerJson = "INVALID_ANSWER_JSON",
    SurveyAlreadySubmitted = "SURVEY_ALREADY_SUBMITTED"
  }

  export namespace Errors {
    
    export interface InvalidSurveyLinkIdDTO {
      code: string;
    }
    
    export class InvalidSurveyLinkId extends LeoRPCError<RPCError> {
      readonly isInvalidSurveyLinkId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidSurveyLinkId;
        super(code);
      }
    
    
      toDTO(): InvalidSurveyLinkIdDTO {
        const dto: InvalidSurveyLinkIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidSurveyLinkId.prototype.toString = function toString(): string {
      return `SaveAndGetNextQuestionRPC.INVALID_SURVEY_LINK_ID()`;
    };
    
    
    export interface InvalidQuestionIdDTO {
      code: string;
    }
    
    export class InvalidQuestionId extends LeoRPCError<RPCError> {
      readonly isInvalidQuestionId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidQuestionId;
        super(code);
      }
    
    
      toDTO(): InvalidQuestionIdDTO {
        const dto: InvalidQuestionIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidQuestionId.prototype.toString = function toString(): string {
      return `SaveAndGetNextQuestionRPC.INVALID_QUESTION_ID()`;
    };
    
    
    export interface SurveyIsClosedDTO {
      code: string;
      surveyIsClosedTitleText: SurveyLocalizedTextDTO;
      surveyIsClosedDescriptionText: SurveyLocalizedTextDTO;
    }
    
    export class SurveyIsClosed extends LeoRPCError<RPCError> {
      readonly isSurveyIsClosed: boolean = true;
    
      readonly surveyIsClosedTitleText: SurveyLocalizedText;
      readonly surveyIsClosedDescriptionText: SurveyLocalizedText;
    
      constructor(
      surveyIsClosedTitleText: SurveyLocalizedText, 
      surveyIsClosedDescriptionText: SurveyLocalizedText
      ) {
        const code = RPCError.SurveyIsClosed;
        super(code);
        this.surveyIsClosedTitleText = surveyIsClosedTitleText;
        this.surveyIsClosedDescriptionText = surveyIsClosedDescriptionText;
      }
    
      static fromDTO(dto: SurveyIsClosedDTO): SurveyIsClosed {
        const surveyIsClosedTitleText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedTitleText", InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError));
        const surveyIsClosedDescriptionText = SurveyLocalizedText.fromDTO(getObject(dto, "surveyIsClosedDescriptionText", InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError));
        return new SurveyIsClosed(
          surveyIsClosedTitleText,
          surveyIsClosedDescriptionText
        );
      }
    
      toDTO(): SurveyIsClosedDTO {
        const dto: SurveyIsClosedDTO = {
          code: this.code,
          surveyIsClosedTitleText: this.surveyIsClosedTitleText.toDTO(),
          surveyIsClosedDescriptionText: this.surveyIsClosedDescriptionText.toDTO()
        };
        return dto;
      }
    
      }
    
    SurveyIsClosed.prototype.toString = function toString(): string {
      return `SaveAndGetNextQuestionRPC.SURVEY_IS_CLOSED(surveyIsClosedTitleText='${this.surveyIsClosedTitleText}',surveyIsClosedDescriptionText='${this.surveyIsClosedDescriptionText}')`;
    };
    
    
    export interface InvalidAnswerJsonDTO {
      code: string;
    }
    
    export class InvalidAnswerJson extends LeoRPCError<RPCError> {
      readonly isInvalidAnswerJson: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidAnswerJson;
        super(code);
      }
    
    
      toDTO(): InvalidAnswerJsonDTO {
        const dto: InvalidAnswerJsonDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidAnswerJson.prototype.toString = function toString(): string {
      return `SaveAndGetNextQuestionRPC.INVALID_ANSWER_JSON()`;
    };
    
    
    export interface SurveyAlreadySubmittedDTO {
      code: string;
      thankYouPageText: SurveyLocalizedTextDTO;
    }
    
    export class SurveyAlreadySubmitted extends LeoRPCError<RPCError> {
      readonly isSurveyAlreadySubmitted: boolean = true;
    
      readonly thankYouPageText: SurveyLocalizedText;
    
      constructor(
      thankYouPageText: SurveyLocalizedText
      ) {
        const code = RPCError.SurveyAlreadySubmitted;
        super(code);
        this.thankYouPageText = thankYouPageText;
      }
    
      static fromDTO(dto: SurveyAlreadySubmittedDTO): SurveyAlreadySubmitted {
        const thankYouPageText = SurveyLocalizedText.fromDTO(getObject(dto, "thankYouPageText", InvalidResponseSurveyAlreadySubmittedThankYouPageTextError));
        return new SurveyAlreadySubmitted(
          thankYouPageText
        );
      }
    
      toDTO(): SurveyAlreadySubmittedDTO {
        const dto: SurveyAlreadySubmittedDTO = {
          code: this.code,
          thankYouPageText: this.thankYouPageText.toDTO()
        };
        return dto;
      }
    
      }
    
    SurveyAlreadySubmitted.prototype.toString = function toString(): string {
      return `SaveAndGetNextQuestionRPC.SURVEY_ALREADY_SUBMITTED(thankYouPageText='${this.thankYouPageText}')`;
    };
    

    export type Errors = InvalidSurveyLinkId | InvalidQuestionId | SurveyIsClosed | InvalidAnswerJson | SurveyAlreadySubmitted;
    export type ErrorsDTO = InvalidSurveyLinkIdDTO | InvalidQuestionIdDTO | SurveyIsClosedDTO | InvalidAnswerJsonDTO | SurveyAlreadySubmittedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidSurveyLinkId:
          return new InvalidSurveyLinkId();
        case RPCError.InvalidQuestionId:
          return new InvalidQuestionId();
        case RPCError.SurveyIsClosed:
          return SurveyIsClosed.fromDTO(dto as SurveyIsClosedDTO);
        case RPCError.InvalidAnswerJson:
          return new InvalidAnswerJson();
        case RPCError.SurveyAlreadySubmitted:
          return SurveyAlreadySubmitted.fromDTO(dto as SurveyAlreadySubmittedDTO);
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestCurrentQuestionIdError extends SaveAndGetNextQuestionRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestCurrentQuestionIdError";
  }
}

export class InvalidRequestSurveyLinkIdError extends SaveAndGetNextQuestionRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestSurveyLinkIdError";
  }
}

export class InvalidRequestAnswerJsonError extends SaveAndGetNextQuestionRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestAnswerJsonError";
  }
}

export class InvalidRequestAnswerJsonNonEmptyError extends InvalidRequestAnswerJsonError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestAnswerJsonNonEmptyError";
  }
}

export class InvalidResponseNextQuestionStatusError extends SaveAndGetNextQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNextQuestionStatusError";
  }
}

export class InvalidResponseNextQuestionStatusAllQuestionsAttemptedError extends InvalidResponseNextQuestionStatusError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNextQuestionStatusAllQuestionsAttemptedError";
  }
}

export class InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionError extends InvalidResponseNextQuestionStatusAllQuestionsAttemptedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionError";
  }
}

export class InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionThankYouPageError extends InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionThankYouPageError";
  }
}

export class InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionThankYouPageThankYouPageTextError extends InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionThankYouPageError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionThankYouPageThankYouPageTextError";
  }
}

export class InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionSubmitConfirmationError extends InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionSubmitConfirmationError";
  }
}

export class InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionSubmitConfirmationSubmitConfirmationDialogPropertiesError extends InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionSubmitConfirmationError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionSubmitConfirmationSubmitConfirmationDialogPropertiesError";
  }
}

export class InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionError extends InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionError";
  }
}

export class InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionPromptUnansweredQuestionsDialogPropertiesError extends InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionPromptUnansweredQuestionsDialogPropertiesError";
  }
}

export class InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionProceedActionError extends InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionProceedActionError";
  }
}

export class InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionProceedActionSubmitConfirmationError extends InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionProceedActionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionProceedActionSubmitConfirmationError";
  }
}

export class InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionProceedActionSubmitConfirmationSubmitConfirmationDialogPropertiesError extends InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionProceedActionSubmitConfirmationError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionProceedActionSubmitConfirmationSubmitConfirmationDialogPropertiesError";
  }
}

export class InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionProceedActionSubmitSurveyError extends InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionProceedActionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNextQuestionStatusAllQuestionsAttemptedNextActionPromptUnansweredQuestionProceedActionSubmitSurveyError";
  }
}

export class InvalidResponseNextQuestionStatusNextQuestionError extends InvalidResponseNextQuestionStatusError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNextQuestionStatusNextQuestionError";
  }
}

export class InvalidResponseNextQuestionStatusNextQuestionRespondentQuestionDetailsError extends InvalidResponseNextQuestionStatusNextQuestionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNextQuestionStatusNextQuestionRespondentQuestionDetailsError";
  }
}

export class InvalidResponseNextQuestionStatusNextQuestionBacktrackedAnswerJsonError extends InvalidResponseNextQuestionStatusNextQuestionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNextQuestionStatusNextQuestionBacktrackedAnswerJsonError";
  }
}

export class InvalidResponseNextQuestionStatusNextQuestionBacktrackedAnswerJsonNonEmptyError extends InvalidResponseNextQuestionStatusNextQuestionBacktrackedAnswerJsonError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNextQuestionStatusNextQuestionBacktrackedAnswerJsonNonEmptyError";
  }
}

export class InvalidResponseNextQuestionStatusNextQuestionSubmitConfirmationButtonTextError extends InvalidResponseNextQuestionStatusNextQuestionError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseNextQuestionStatusNextQuestionSubmitConfirmationButtonTextError";
  }
}

export class InvalidResponseProgressPercentageError extends SaveAndGetNextQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProgressPercentageError";
  }
}

export class InvalidResponseInvalidSurveyLinkIdError extends SaveAndGetNextQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidSurveyLinkIdError";
  }
}

export class InvalidResponseInvalidQuestionIdError extends SaveAndGetNextQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidQuestionIdError";
  }
}

export class InvalidResponseSurveyIsClosedError extends SaveAndGetNextQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedTitleTextError";
  }
}

export class InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError extends InvalidResponseSurveyIsClosedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyIsClosedSurveyIsClosedDescriptionTextError";
  }
}
export class InvalidResponseInvalidAnswerJsonError extends SaveAndGetNextQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidAnswerJsonError";
  }
}

export class InvalidResponseSurveyAlreadySubmittedError extends SaveAndGetNextQuestionRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyAlreadySubmittedError";
  }
}

export class InvalidResponseSurveyAlreadySubmittedThankYouPageTextError extends InvalidResponseSurveyAlreadySubmittedError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyAlreadySubmittedThankYouPageTextError";
  }
}
